import { useState, useEffect, useCallback } from 'react';

import { useNavigate } from 'react-router-dom';

import { useServices } from '@/contexts';
import { getErrorMessage } from '@shared/utils/errorHandler';

interface ErrorState {
  message: string;
  action?: string;
}

interface UseLoginReturn {
  loading: boolean;
  isRedirecting: boolean;
  error: ErrorState | null;
  handleLogin: () => Promise<void>;
}

/** Starts the OAuth login redirect, surfacing a readable error on failure; always clears loading. */
async function startLogin(
  authService: ReturnType<typeof useServices>['authService'],
  setLoading: (loading: boolean) => void,
  setError: (error: ErrorState | null) => void,
  setIsRedirecting: (redirecting: boolean) => void
): Promise<void> {
  try {
    setLoading(true);
    setError(null);
    setIsRedirecting(true);
    // Always use remember me = true to persist login for 30 days
    await authService.login(true);
  } catch (err) {
    const errorObj = getErrorMessage(err instanceof Error ? err.message : 'Unknown error');
    setError(errorObj);
    setIsRedirecting(false);
  } finally {
    setLoading(false);
  }
}

export const useLogin = (): UseLoginReturn => {
  const navigate = useNavigate();
  const { authService } = useServices();
  const [loading, setLoading] = useState<boolean>(false);
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
  const [error, setError] = useState<ErrorState | null>(null);

  // Redirect if token is already present
  useEffect(() => {
    // Check for token in URL (after OAuth redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
      try {
        authService.setToken(token);
        navigate('/');
      } catch (error) {
        const errorObj = getErrorMessage(error instanceof Error ? error.message : 'Unknown error');
        setError(errorObj);
      }

      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [navigate, authService]);

  const handleLogin = useCallback(async (): Promise<void> => {
    await startLogin(authService, setLoading, setError, setIsRedirecting);
  }, [authService]);

  return {
    loading,
    isRedirecting,
    error,
    handleLogin,
  };
};
